import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import ComicCard from "@/components/common/ComicCard";
import Breadcrumb from "@/components/common/Breadcrumb";
import { categories, getCategoryBySlug, getComicsByCategory } from "@/mocks/comics";

export default function Category() {
  const { slug = "" } = useParams();
  const category = getCategoryBySlug(slug);
  const list = getComicsByCategory(slug);
  const others = categories.filter((c) => c.slug !== slug);

  useSeo({
    title: `${category ? `${category.name} - ` : ""}口碑漫画推荐与补番清单 | 我爱漫画网`,
    description: category
      ? `${category.description} 我爱漫画网为你精选${category.name}，收录口碑漫画、高分漫画与冷门佳作，帮你快速找到好漫画。`
      : "漫画推荐与补番清单，帮你快速找到好漫画。",
    path: `/category/${slug}`,
  });

  if (!category) {
    return (
      <div className="container-page flex flex-col items-center py-24 text-center">
        <h1 className="font-heading text-2xl font-bold text-foreground-950">栏目不存在</h1>
        <p className="mt-3 text-sm text-foreground-600">你要找的栏目可能已被调整，去推荐榜单看看吧。</p>
        <Link to="/rankings" className="mt-6 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
          去推荐榜单
        </Link>
      </div>
    );
  }

  const itemList = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 我爱漫画网`,
    description: category.description,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: list.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.title,
        url: `https://faeverse.github.io/comic/${c.id}`,
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemList) }} />

      <section className="relative overflow-hidden bg-foreground-950 text-background-50">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-foreground-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(255,255,255,0.12),transparent_40%)]" />
        <div className="container-page relative z-10 flex flex-col items-start py-14 md:py-20">
          <span className="mb-4 flex items-center gap-2 rounded-full bg-background-50/10 px-4 py-1.5 text-sm text-background-100 backdrop-blur-sm">
            <i className={`${category.icon} text-accent-400`} />
            我爱漫画网 · {category.name}
          </span>
          <h1 className="font-heading text-3xl font-bold md:text-4xl">{category.name}</h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
            {category.description}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {category.subtitle.split(" · ").map((t) => (
              <span key={t} className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-background-100">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "推荐榜单", to: "/rankings" },
          { label: category.name },
        ]}
      />

      <section className="container-page pb-12 md:pb-16">
        <p className="mb-5 text-sm text-foreground-500">
          本栏目收录 <span className="font-bold text-primary-600">{list.length}</span> 部作品
        </p>
        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {list.map((comic) => (
              <ComicCard key={comic.id} comic={comic} showCategory={false} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-600">
              <i className="ri-emotion-sad-line" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">这个栏目暂时还在整理中</h2>
            <p className="mt-2 text-sm text-foreground-600">编辑部正在补全内容，先去其他栏目逛逛吧。</p>
          </div>
        )}
      </section>

      <section className="bg-background-100">
        <div className="container-page py-10 md:py-12">
          <h2 className="font-heading text-lg font-bold text-foreground-950">去其他栏目逛逛</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700 group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={c.icon} />
                </span>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-700">{c.name}</h3>
                  <p className="truncate text-xs text-foreground-500">{c.subtitle}</p>
                </div>
                <i className="ri-arrow-right-s-line ml-auto text-lg text-foreground-400" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}